import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../lib/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1. Unified Logout Function (Wrapped in useCallback to prevent re-renders)
  const logout = useCallback(() => {
    localStorage.clear(); 
    setUser(null);
    // Use window.location only if we need a hard reset, 
    // otherwise React Router handles the redirect.
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, []);

  // 2. Initialize Auth on Load
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const { data } = await api.get('/auth/me');
          if (data.success) {
            setUser(data.user);
          }
        } catch (error) {
          console.error("Session expired or invalid:", error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [logout]);

  // 3. Login Function
  const login = async (username, password) => {
    try {
      const { data } = await api.post('/auth/login', { username, password });
      
      if (data.success) {
        // Sync everything to LocalStorage
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('username', data.user.username);
        localStorage.setItem('role', data.user.role);
        localStorage.setItem('api_key', data.user.api_key);
        localStorage.setItem('balance', data.user.balance || 0);
        
        // Update State
        setUser(data.user);
        return data.user; 
      }
    } catch (error) {
      throw error; // Let the Login page handle the UI error message
    }
  };

  // 4. Register Function (Added for completeness)
  const register = async (userData) => {
    try {
      const { data } = await api.post('/auth/register', userData);
      return data;
    } catch (error) {
      throw error;
    }
  };

  // 5. Update Balance (Useful for when a user tops up or sends a message)
  const updateBalance = (newBalance) => {
    setUser(prev => prev ? { ...prev, balance: newBalance } : null);
    localStorage.setItem('balance', newBalance);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      register, 
      updateBalance, 
      loading 
    }}>
      {/* Show nothing or a global spinner until we know if the user is logged in */}
      {!loading ? children : (
        <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};